import { FC } from "react";
import { useSnackbar } from "notistack";

import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import { useUpdateAssetValidation } from "@/Features/AssetValidations/asset-validations.query";
import type { AssetValidation } from "@/Features/AssetValidations/asset-validations.types";
import DefaultValidationForm from "./DefaultValidationForm";
import {
  ValidationDirtyFields,
  ValidationFormData,
  toUpdateValidationBody,
  toValidationFormData,
} from "./validationForm.utils";

interface EditValidationFormProps {
  validation: AssetValidation;
  // Resolved name for `completed_by_id` (the api returns ids only). Lets the
  // selector show and clear a user the active-only list no longer contains.
  completedByName?: string;
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

// Documents are not edited here: a validation's reports are listed in the
// flyout (ENG-1673) and uploaded through the create flow. The mappers live in
// validationForm.utils.ts (pure, unit-tested); this only wires them to the
// shared form and the mutation. Same shape as EditLabAssetForm.
const EditValidationForm: FC<EditValidationFormProps> = ({
  validation,
  completedByName,
  width,
  onSubmit,
  onCancel,
  onSubmittingChange,
  onDirtyChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const updateValidation = useUpdateAssetValidation();

  // UserSelector drops the pin when the active list already has the id, so
  // the suffix only shows for the excluded case.
  const completedByPinnedOption =
    validation.completed_by_id != null
      ? {
          value: validation.completed_by_id,
          label: `${
            completedByName || `User ${validation.completed_by_id}`
          } (inactive)`,
        }
      : undefined;

  const handleSubmit = async (
    data: ValidationFormData,
    dirtyFields: ValidationDirtyFields
  ) => {
    const body = toUpdateValidationBody(data, dirtyFields);
    // Save is disabled while clean, so this only guards a race; an empty
    // body would be a 400 ("No fields to update").
    if (Object.keys(body).length === 0) return;

    try {
      // Throws with the server's message (400/403/404) and has already opted
      // out of the interceptor toast — one snackbar. The api writes the
      // activity-log entry for the update.
      await updateValidation.mutateAsync({ uuid: validation.uuid, body });
      onSubmit?.();
      enqueueSnackbar(
        `Validation ${data.version ? `${data.version} ` : ""}updated`,
        { variant: "success" }
      );
    } catch (error) {
      // Modal stays open with the entered values intact.
      console.error(error);
      enqueueSnackbar(
        error instanceof Error && error.message
          ? error.message
          : "An error occurred while updating this validation. Please try again.",
        { variant: "error" }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <DefaultValidationForm
        DefaultFormData={toValidationFormData(validation)}
        showDocuments={false}
        completedByPinnedOption={completedByPinnedOption}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onSubmittingChange={onSubmittingChange}
        onDirtyChange={onDirtyChange}
        disableSubmitWhenClean
        buttonProps={{ submitText: "Save Changes" }}
      />
    </FormWrapper>
  );
};

export default EditValidationForm;
